import { useEffect, useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { getData } from 'services/api';
import { Page } from 'features/Orders/types';
import { countTicketsByType } from 'features/common/utils/compedTickets';

const PER_PAGE = 100;

/**
 * Aggregates per-ticket-type comped counts across all loaded order pages.
 * Per-order quantity comes from each order's per-ticket `tickets` array (see
 * `countTicketsByType` — counting the deduped `tickets_name` would report 1 per
 * type regardless of how many were comped).
 *
 * Shared analytics hook: consumed by the Ticket Inventory section (comped donut)
 * and the Show Performance KPI section (revenue-weighted Comp Rate). Lives at the
 * Analytics feature root so neither section reaches into the other.
 *
 * `countByTypeName` exported for unit testing only — not part of the public API.
 */
export const countByTypeName = (pages: Page[]): Record<string, number> => {
	const counts: Record<string, number> = {};
	for (const page of pages) {
		for (const order of page.data) {
			const orderCounts = countTicketsByType(order.tickets, order.tickets_name);
			for (const name in orderCounts) {
				counts[name] = (counts[name] ?? 0) + orderCounts[name];
			}
		}
	}
	return counts;
};

/**
 * Pagination terminator for the comped-order auto-exhaust below. Continues only
 * while the last page came back full AND fewer than `total` rows have been
 * loaded. The `total` guard is what stops a backend that keeps returning full
 * `PER_PAGE` pages from paginating forever — without it, the auto-exhaust effect
 * fires `fetchNextPage` endlessly (a flood of /orders requests). Exported for
 * unit testing only.
 */
export const getNextCompedPageParam = (lastPage: Page, pages: Page[]): number | undefined => {
	const loaded = pages.reduce((sum, page) => sum + page.data.length, 0);
	return lastPage.data.length >= PER_PAGE && loaded < lastPage.total ? pages.length + 1 : undefined;
};

export const useCompedInventory = (eventId: string): Record<string, number> => {
	const query = useInfiniteQuery({
		queryKey: ['comped-inventory', eventId],
		queryFn: ({ pageParam = 1 }: { pageParam?: number }) =>
			getData(`/orders?comped=true&event_ids=${eventId}&page=${pageParam}&per_page=${PER_PAGE}`),
		getNextPageParam: getNextCompedPageParam,
		enabled: !!eventId,
	});

	// Auto-exhaust pages so counts are always complete when the hook value settles.
	useEffect(() => {
		if (query.hasNextPage && !query.isFetchingNextPage) {
			query.fetchNextPage();
		}
	}, [query.hasNextPage, query.isFetchingNextPage, query.fetchNextPage]);

	return useMemo(() => (query.data ? countByTypeName(query.data.pages as Page[]) : {}), [query.data]);
};
